import { RedoOutlined, UserOutlined, CarOutlined, MailOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { Space, Input, Popover, Tag, Popconfirm, Button } from 'antd';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeToolbar, NdoeActionButton,
  NdoeResponsiveTable, NdoeDataCard, NdoeStatusBadge,
} from '../../components/ndoe';

const Livreur = () => {
    const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
    const user = useSelector((state) => state.user?.currentUser);
    // Seul l'admin bascule un compte (la route l'exige aussi). On MASQUE le
    // bouton plutôt que de laisser cliquer pour un refus : proposer une action
    // qu'on sait interdite est une promesse qu'on ne tient pas.
    const estAdmin = user?.role === 'admin';
    /*
      DÉCLARÉE ICI, AVANT les colonnes qui la lisent.

      Placée plus bas, elle « marchait » parce que le rendu d'une colonne est
      différé — mais c'est la zone morte temporelle qui a déjà fait tomber cette
      application entière (routeur API, 10/08). On ne s'appuie pas là-dessus.
    */
    const [bascule, setBascule] = useState(null);
    // `isAdmin` gardait le bouton « Supprimer », retiré le 10/08/2026. La
    // variable reste utile le jour où une action réservée reviendra ici.
    // eslint-disable-next-line no-unused-vars
    const isAdmin = user?.role === 'admin';
    const [getLivreur, setGetLivreur] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');

    const columns = [
        { title: '#', dataIndex: 'id', key: 'id', render: (text, record, index) => index + 1 },
        {
            title: 'Nom',
            dataIndex: 'username',
            key: 'username',
            render: (text) => (
              <Tag color={'blue'}><UserOutlined style={{ marginRight: "5px" }} />{text}</Tag>
            ),
        },
        {
          title: 'Email',
          dataIndex: 'email',
          key: 'email',
          render: (text) => (
            <Popover content="Cliquez pour ouvrir Gmail" trigger="hover">
              <Tag color="yellow">
                <a href={`mailto:${text}`} target="_blank" rel="noopener noreferrer">
                  <MailOutlined style={{ marginRight: '5px' }} />
                  {text}
                </a>
              </Tag>
            </Popover>
          ),
        },
        {
          title: 'Role',
          dataIndex: 'role',
          key: 'role',
          render: (text) => (
            <Tag color="green">
              <CarOutlined style={{ marginRight: '5px' }} />
              {text}
            </Tag>
          ),
        },
        {
          /*
            L'ÉTAT DU COMPTE — ce qui remplace le « Supprimer » retiré le 10/08.

            Supprimer un livreur orphelinerait ses livraisons : le plus ancien en
            porte 2556. Désactiver ferme l'AVENIR et laisse le PASSÉ intact — son
            nom reste sur chaque livraison, chaque rapport, chaque vente.
          */
          title: 'État',
          key: 'actif',
          render: (_, record) => (
            Number(record.actif) === 0
              ? <Tag color="default">Ne travaille plus ici</Tag>
              : <Tag color="green">Actif</Tag>
          ),
        },
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => (
              <Space size="middle">
                {/* « Supprimer » a été retiré (il orphelinerait les livraisons).
                    À sa place : fermer l'avenir sans toucher au passé. */}
                {estAdmin && (
                <Popconfirm
                  title={Number(record.actif) === 0
                    ? `Réactiver le compte de ${record.username} ?`
                    : `${record.username} ne travaille plus ici ?`}
                  description={Number(record.actif) === 0
                    ? 'Il pourra de nouveau se connecter et recevoir des missions.'
                    : 'Son compte sera fermé et ses sessions coupées. Ses livraisons passées gardent son nom.'}
                  okText={Number(record.actif) === 0 ? 'Réactiver' : 'Désactiver'}
                  cancelText="Annuler"
                  onConfirm={() => basculerActif(record)}
                >
                  <Button size="small" loading={bascule === record.id} danger={Number(record.actif) !== 0}>
                    {Number(record.actif) === 0 ? 'Réactiver' : 'Désactiver'}
                  </Button>
                </Popconfirm>
                )}
              </Space>
            ),
          },
      ];

      /*
        BASCULER L'ÉTAT D'UN COMPTE.

        Le serveur porte les règles : il REFUSE de désactiver quelqu'un qui a
        encore des paires dehors, et dit combien et sur quelles commandes. On
        affiche ce refus TEL QUEL — il est écrit pour être lu, et le réécrire ici
        le ferait diverger le jour où la règle changera.
      */
      const basculerActif = async (record) => {
        const versActif = Number(record.actif) === 0;
        setBascule(record.id);
        try {
          await axios.put(`${DOMAIN}/api/livreur/${record.id}/actif`, { actif: versActif });
          toast.success(versActif
            ? `${record.username} peut de nouveau travailler.`
            : `${record.username} ne recevra plus de mission.`);
          await fetchData();
        } catch (e) {
          toast.error(e?.response?.data?.error || "La modification n'a pas pu être enregistrée.");
        } finally {
          setBascule(null);
        }
      };

      // Fetch réutilisable (utilisé au montage, au rafraîchissement et après mutation).
      const fetchData = async () => {
        try {
          setLoading(true);
          // `tous=1` : ailleurs la liste ne sert qu'à CHOISIR un livreur et
          // n'affiche que les actifs. Ici c'est l'écran de GESTION — sans les
          // comptes fermés, on ne pourrait plus les rouvrir.
          const { data } = await axios.get(`${DOMAIN}/api/livreur`, { params: { tous: 1 } });
          setGetLivreur(data);
        } catch (error) {
          toast.error('Erreur lors du chargement des livreurs');
        } finally {
          setLoading(false);
        }
      };

      useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

/*
 * LE BOUTON « SUPPRIMER » A ÉTÉ RETIRÉ (10/08/2026, décision de la propriétaire).
 *
 * Il n'a JAMAIS fonctionné : il appelait `PUT /api/livreur/:id`, une route qui
 * n'existe pas (seul le DELETE est déclaré). Le clic tombait en 404 et le
 * livreur restait, après une alerte d'erreur.
 *
 * ON NE L'A PAS "RÉPARÉ", ET C'EST DÉLIBÉRÉ. Le faire marcher aurait été bien
 * pire que le laisser cassé, pour trois raisons mesurées le 10/08 :
 *
 *  1. CETTE LISTE MONTRE DES COMPTES `users` (role = 'livreur'), pas la table
 *     `livreur`. Or la suppression visait `DELETE FROM livreur WHERE
 *     id_livreur = ?`. Deux tables, deux espaces d'identifiants : la table
 *     `livreur` ne contient qu'UNE ligne (« Melo », id 2) sans aucun rapport
 *     avec les six comptes listés ici (ids 5, 6, 9, 13, 14, 16).
 *
 *  2. LES LIVRAISONS POINTENT VERS `users.id`. Elie en porte 2 556, Heritier
 *     2 130. Supprimer le compte orphelinerait tout cet historique.
 *
 *  3. AUCUNE CONTRAINTE DE CLÉ ÉTRANGÈRE ne protège les huit tables qui
 *     référencent un livreur (delivery_event, detail_livraison, depenses,
 *     mission, tournee, vente…). La base ne dirait rien ; les rapports
 *     afficheraient des livraisons faites par quelqu'un qui n'existe plus.
 *
 * Une vraie désactivation demande une colonne dédiée sur `users` et son respect
 * partout où l'on liste les livreurs — un chantier à part, pas un correctif.
 */

      const Rafraichir = () => {
        fetchData();
        toast.success('Liste actualisée');
      };

       const filteredData = getLivreur?.filter((item) =>
        item.username?.toLowerCase().includes(searchValue.toLowerCase())
      )

      // Carte mobile : nom, email, badge rôle, action Supprimer (garde admin — Lot 0).
      const renderLivreurCard = (record) => (
        <NdoeDataCard
          title={record.username}
          subtitle={record.email}
          badge={<NdoeStatusBadge kind="custom" tone="info" label={record.role} />}
          // Action « Supprimer » retirée — voir l'explication au-dessus de la
          // liste.
          actions={null}
        />
      );

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Logistique"
        title="Liste des livreurs"
        subtitle="Gérez vos livreurs"
      />

      <NdoeToolbar
        search={
          <Input.Search
            type="search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Rechercher un livreur…"
            allowClear
            style={{width:'250px'}}
          />
        }
      >
        <NdoeActionButton variant="secondary" icon={<RedoOutlined />} onClick={Rafraichir}>
          Actualiser
        </NdoeActionButton>
      </NdoeToolbar>

      <NdoeResponsiveTable
        data={filteredData}
        columns={columns}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10 }}
        renderCard={renderLivreurCard}
        empty={{ icon: '🚚', title: 'Aucun livreur', text: 'Ajustez votre recherche.' }}
      />
    </NdoePage>
  )
}

export default Livreur
